import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Chip, Switch, FormControlLabel, Alert, Snackbar, Tooltip, Grid, Divider, Tabs, Tab
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Email as EmailIcon,
  Preview as PreviewIcon, Visibility as VisibilityIcon, DataObject as DataObjectIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import EmailTemplateService from '../../services/emailTemplate.service';
import http from '../../http-common';

const SAMPLE_TEMPLATE_DATA = {
  client_name: 'John Doe',
  client_email: 'john@example.com',
  client_phone: '+1234567890',
  anniversary_date: '2023-01-15',
  anniversary_date_formatted: 'January 15, 2023',
  days_until_anniversary: 7,
  years_since_anniversary: 1,
  current_date: new Date().toISOString().slice(0, 10),
  current_date_formatted: new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }),
};

const renderTemplateLocally = (template, sampleData = SAMPLE_TEMPLATE_DATA) => {
  let subject = template.subject || '';
  let body = template.body || '';

  Object.entries(sampleData).forEach(([key, value]) => {
    const token = `{${key}}`;
    subject = subject.split(token).join(String(value));
    body = body.split(token).join(String(value));
  });

  return { subject, body, sample_data: sampleData };
};

const EmailTemplateManagement = () => {
  const { hasPermission, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tabValue, setTabValue] = useState(0);
  const [previewData, setPreviewData] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [senderInfo, setSenderInfo] = useState(null);
  const [activeField, setActiveField] = useState('body');
  const [availableVariables, setAvailableVariables] = useState([]);
  const subjectInputRef = useRef(null);
  const bodyInputRef = useRef(null);

  const variableInfo = {
    client_name: { label: 'Client Name', example: 'John Doe' },
    client_email: { label: 'Client Email', example: 'john@example.com' },
    client_phone: { label: 'Client Phone', example: '+1234567890' },
    anniversary_date: { label: 'Anniversary Date', example: '2023-01-15' },
    anniversary_date_formatted: { label: 'Anniversary Date (formatted)', example: 'January 15, 2023' },
    days_until_anniversary: { label: 'Days Until Anniversary', example: '7' },
    years_since_anniversary: { label: 'Years Since Anniversary', example: '1' },
    anniversary_type: { label: 'Anniversary Type', example: 'Birthday' },
    current_date: { label: 'Current Date', example: '2025-06-24' },
    current_date_formatted: { label: 'Current Date (formatted)', example: 'June 24, 2025' },
  };

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    type: 'anniversary',
    is_active: true,
    variables: []
  });

  const templateTypes = [
    { value: 'anniversary', label: 'Anniversary', color: 'primary' },
    { value: 'birthday', label: 'Birthday', color: 'secondary' },
    { value: 'appointment_reminder', label: 'Appointment Reminder', color: 'success' },
    { value: 'custom', label: 'Custom', color: 'info' }
  ];

  useEffect(() => {
    fetchTemplates();
    http.get('/settings/email-sender')
      .then((res) => setSenderInfo(res.data))
      .catch((err) => console.error('Error loading sender info:', err));
  }, []);

  useEffect(() => {
    if (!openDialog) {
      return;
    }

    const loadVariables = async () => {
      try {
        const response = await EmailTemplateService.getVariables(formData.type);
        const apiVariables = response.data.variables || [];
        const formattedExtras = ['anniversary_date_formatted', 'current_date_formatted'];
        const merged = [...new Set([...apiVariables, ...formattedExtras])];
        setAvailableVariables(merged);
      } catch (error) {
        console.error('Error loading variables:', error);
        setAvailableVariables([
          'client_name', 'client_email', 'client_phone', 'anniversary_date',
          'anniversary_date_formatted', 'days_until_anniversary', 'years_since_anniversary',
          'current_date', 'current_date_formatted'
        ]);
      }
    };

    loadVariables();
  }, [openDialog, formData.type]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await EmailTemplateService.getAll();
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
      showSnackbar('Error loading templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        subject: template.subject,
        body: template.body,
        type: template.type,
        is_active: template.is_active,
        variables: template.variables || []
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: '',
        subject: '',
        body: '',
        type: 'anniversary',
        is_active: true,
        variables: []
      });
    }
    setOpenDialog(true);
    setActiveField('body');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      subject: '',
      body: '',
      type: 'anniversary',
      is_active: true,
      variables: []
    });
    setTabValue(0);
    setActiveField('body');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setTabValue(0);
      showSnackbar('Template name is required', 'error');
      return false;
    }

    if (!formData.subject.trim()) {
      setTabValue(1);
      showSnackbar('Email subject is required. Go to the Content tab to add it.', 'error');
      return false;
    }

    if (!formData.body.trim()) {
      setTabValue(1);
      showSnackbar('Email body is required. Go to the Content tab to add it.', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (editingTemplate) {
        await EmailTemplateService.update(editingTemplate.id, formData);
        showSnackbar('Template updated successfully');
      } else {
        await EmailTemplateService.create(formData);
        showSnackbar('Template created successfully');
      }
      handleCloseDialog();
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);

      let errorMessage = 'Error saving template';
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await EmailTemplateService.delete(id);
        showSnackbar('Template deleted successfully');
        fetchTemplates();
      } catch (error) {
        console.error('Error deleting template:', error);
        showSnackbar('Error deleting template', 'error');
      }
    }
  };

  const handlePreview = async (template) => {
    setPreviewTemplate(template);
    setTestEmail(user?.email || '');
    setPreviewData(renderTemplateLocally(template));
    setPreviewDialog(true);

    try {
      const response = await EmailTemplateService.preview(template.id);
      setPreviewData(response.data);
    } catch (error) {
      console.error('Error previewing template:', error);
      showSnackbar('Showing local preview (API preview unavailable)', 'warning');
    }
  };

  const handleClosePreview = () => {
    setPreviewDialog(false);
    setPreviewData(null);
    setPreviewTemplate(null);
    setTestEmail('');
    setSendingTest(false);
  };

  const handleSendTestEmail = async () => {
    if (!previewTemplate) {
      return;
    }

    if (!testEmail.trim()) {
      showSnackbar('Please enter an email address', 'error');
      return;
    }

    try {
      setSendingTest(true);
      await EmailTemplateService.sendTest(previewTemplate.id, testEmail.trim());
      showSnackbar('Test email sent — check your inbox and spam folder');
    } catch (error) {
      console.error('Error sending test email:', error);
      const errorMessage = error.response?.data?.error
        || error.response?.data?.message
        || 'Error sending test email';
      showSnackbar(errorMessage, 'error');
    } finally {
      setSendingTest(false);
    }
  };

  const insertVariable = (variable, field = activeField) => {
    const token = `{${variable}}`;
    const inputRef = field === 'subject' ? subjectInputRef : bodyInputRef;
    const input = inputRef.current;

    if (input) {
      const start = input.selectionStart ?? formData[field].length;
      const end = input.selectionEnd ?? start;
      const text = formData[field];
      const newText = text.substring(0, start) + token + text.substring(end);

      setFormData(prev => ({
        ...prev,
        [field]: newText
      }));

      setTimeout(() => {
        input.focus();
        const cursor = start + token.length;
        input.setSelectionRange(cursor, cursor);
      }, 0);
      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: prev[field] + token
    }));
  };

  const renderVariablePicker = () => (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        bgcolor: 'grey.50',
        height: '100%',
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <DataObjectIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2">Insert Variables</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        Click a variable to insert it into the{' '}
        <strong>{activeField === 'subject' ? 'subject' : 'body'}</strong>.
        Focus a field first to choose where it goes.
      </Typography>
      <Box display="flex" flexDirection="column" gap={1}>
        {availableVariables.map((variable) => {
          const info = variableInfo[variable] || {
            label: variable.replace(/_/g, ' '),
            example: '',
          };

          return (
            <Tooltip
              key={variable}
              title={info.example ? `Example: ${info.example}` : `Insert {${variable}}`}
              placement="left"
            >
              <Chip
                label={
                  <Box>
                    <Typography variant="caption" component="span" sx={{ fontFamily: 'monospace' }}>
                      {`{${variable}}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {info.label}
                    </Typography>
                  </Box>
                }
                onClick={() => insertVariable(variable)}
                variant="outlined"
                clickable
                sx={{
                  height: 'auto',
                  py: 0.75,
                  justifyContent: 'flex-start',
                  '& .MuiChip-label': { display: 'block', whiteSpace: 'normal' },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );

  const getTypeColor = (type) => {
    const templateType = templateTypes.find(t => t.value === type);
    return templateType ? templateType.color : 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading templates...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" component="h1">
              Email Template Management
            </Typography>
            {hasPermission('emails.create') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Create Template
              </Button>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" mb={3}>
            Manage email templates for anniversary reminders and other automated communications.
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Variables</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <EmailIcon color="primary" />
                        <Typography variant="body2" fontWeight="medium">
                          {template.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={templateTypes.find(t => t.value === template.type)?.label || template.type}
                        size="small"
                        color={getTypeColor(template.type)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {template.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={template.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={template.is_active ? 'success' : 'default'}
                        variant={template.is_active ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {template.variables?.length || 0} variables
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1}>
                        {hasPermission('emails.view') && (
                          <Tooltip title="Preview template">
                            <IconButton
                              size="small"
                              onClick={() => handlePreview(template)}
                            >
                              <PreviewIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('emails.edit') && (
                          <Tooltip title="Edit template">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(template)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('emails.delete') && (
                          <Tooltip title="Delete template">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(template.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {templates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box py={4}>
                        <EmailIcon color="disabled" sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No templates created
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Create your first email template to get started
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Template Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon color="primary" />
            {editingTemplate ? 'Edit Template' : 'Create Template'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label="Basic Info" />
              <Tab label="Content" />
            </Tabs>
          </Box>

          {tabValue === 0 && (
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <TextField
                label="Template Name"
                value={formData.name}
                onChange={handleChange}
                name="name"
                required
                fullWidth
                placeholder="e.g., Birthday Reminder"
              />

              <FormControl fullWidth>
                <InputLabel>Template Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={handleChange}
                  name="type"
                  label="Template Type"
                >
                  {templateTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip label={type.label} size="small" color={type.color} />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleChange}
                    name="is_active"
                  />
                }
                label="Active template"
              />
            </Box>
          )}

          {tabValue === 1 && (
            <Grid container spacing={2} pt={1}>
              <Grid item xs={12} md={8}>
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    label="Email Subject"
                    value={formData.subject}
                    onChange={handleChange}
                    onFocus={() => setActiveField('subject')}
                    name="subject"
                    required
                    fullWidth
                    inputRef={subjectInputRef}
                    placeholder="e.g., Anniversary Reminder - {client_name}"
                    helperText="Click variables on the right to insert dynamic content"
                  />

                  <TextField
                    label="Email Body"
                    value={formData.body}
                    onChange={handleChange}
                    onFocus={() => setActiveField('body')}
                    name="body"
                    required
                    fullWidth
                    multiline
                    rows={12}
                    inputRef={bodyInputRef}
                    placeholder="Enter your email content here..."
                    helperText="Variables are replaced automatically when the email is sent"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                {renderVariablePicker()}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTemplate ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onClose={handleClosePreview} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <VisibilityIcon color="primary" />
            Template Preview
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewData && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Subject: {previewData.subject || '(No subject)'}
              </Typography>
              {senderInfo && (
                <Box mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    From:{' '}
                    <strong>{senderInfo.effective_from_name}</strong>{' '}
                    &lt;{senderInfo.effective_from_email}&gt;
                    {senderInfo.reply_to && senderInfo.reply_to !== senderInfo.effective_from_email && (
                      <> · Reply-To: {senderInfo.reply_to}</>
                    )}
                  </Typography>
                  {senderInfo.using_default && (
                    <Typography variant="caption" color="warning.main">
                      Using the system default address. A superadmin can set a custom sender in Settings → Sender email.
                    </Typography>
                  )}
                </Box>
              )}
              <Divider sx={{ my: 2 }} />
              {previewData.body ? (
                <Box
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 2,
                    backgroundColor: '#f9f9f9',
                    maxHeight: 400,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {previewData.body}
                </Box>
              ) : (
                <Alert severity="info">
                  This template has no body content yet. Edit the template and add content on the Content tab.
                </Alert>
              )}

              {hasPermission('emails.send') && (
                <Box mt={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Send test email
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Sample data (e.g. John Doe) will be used to fill in the variables.
                  </Typography>
                  <Box display="flex" gap={1} alignItems="flex-start">
                    <TextField
                      label="Recipient email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      type="email"
                      fullWidth
                      placeholder="you@example.com"
                    />
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={handleSendTestEmail}
                      disabled={sendingTest || !testEmail.trim()}
                      sx={{ mt: 1, whiteSpace: 'nowrap' }}
                    >
                      {sendingTest ? 'Sending...' : 'Send Test'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailTemplateManagement;
